import React, { useState, useRef, useEffect } from "react";
import type { ChatMessage } from "./types";
import { formatTime } from "./helpers";
import MessageActions from "./MessageActions";

interface UserMessageProps {
  message: ChatMessage;
  onEdit?: (messageId: string, newContent: string) => void;
  onCopy?: (content: string) => void;
}

export default function UserMessage({ message, onEdit, onCopy }: UserMessageProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(message.content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [editing]);

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== message.content) {
      onEdit?.(message.id, trimmed);
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setEditValue(message.content);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      handleCancel();
    }
  };

  const isNew = message.isNew && Date.now() - message.timestamp < 500;

  return (
    <div className={`flex gap-3 justify-end group ${isNew ? "message-enter" : ""}`}>
      <div className="max-w-[80%]">
        {editing ? (
          <div
            className="rounded-2xl px-4 py-3"
            style={{ backgroundColor: "var(--c-surface)", border: "1px solid var(--c-primary)" }}
          >
            <textarea
              ref={textareaRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full text-sm resize-none focus:outline-none"
              style={{ backgroundColor: "transparent", color: "var(--c-text)", minWidth: 200 }}
              onInput={(e) => {
                const el = e.currentTarget;
                el.style.height = "auto";
                el.style.height = el.scrollHeight + "px";
              }}
            />
            <div className="flex items-center gap-2 mt-2 justify-end">
              <button
                onClick={handleCancel}
                className="px-3 py-1 text-xs rounded-md transition-colors"
                style={{ color: "var(--c-text-secondary)" }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-3 py-1 text-xs rounded-md transition-colors"
                style={{ backgroundColor: "var(--c-primary)", color: "var(--c-primary-text)" }}
              >
                Save & Submit
              </button>
            </div>
          </div>
        ) : (
          <>
            <div
              className="rounded-2xl rounded-br-sm px-4 py-2.5 text-sm"
              style={{ backgroundColor: "var(--c-primary)", color: "var(--c-primary-text)" }}
            >
              <span className="whitespace-pre-wrap user-bubble-text">{message.content}</span>
            </div>
            <div className="flex items-center gap-2 mt-1 justify-end">
              <span className="text-[10px]" style={{ color: "var(--c-text-faint)" }}>
                {formatTime(message.timestamp)}
              </span>
            </div>
            <MessageActions
              role="user"
              content={message.content}
              onCopy={onCopy}
              onEdit={() => setEditing(true)}
              align="right"
            />
          </>
        )}
      </div>
    </div>
  );
}
